/* ───────────────────────────────────
   Everyday Loopie PDP
   Loaded only by sections/everyday-loopie-pdp.liquid.
   Fully self-contained: every selector is elp-* and nothing here depends on
   theme.js having run first, so load order is irrelevant. Where the global
   cart helpers exist they are reused, otherwise it falls back to the Ajax API.
   ─────────────────────────────────── */
(function () {
  'use strict';

  const root = document.querySelector('[data-elp]');
  if (!root) return;

  /* --- helpers --- */
  // theme.js declares `const LoopCart`, which lives in the global lexical scope
  // rather than on `window`, so it has to be reached by bare identifier. This is
  // resolved lazily because theme.js is deferred and runs after this file.
  function cartApi() {
    try {
      if (typeof LoopCart !== 'undefined' && LoopCart) return LoopCart;
    } catch (e) { /* not defined yet */ }
    return window.LoopCart || null;
  }

  function money(cents) {
    const api = cartApi();
    if (api && typeof api.formatMoney === 'function') return api.formatMoney(cents);
    return '₹' + (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  async function addToCart(variantId, quantity) {
    const api = cartApi();
    if (api && typeof api.addItem === 'function') {
      return api.addItem(variantId, quantity);
    }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity }),
    });
    if (!res.ok) throw new Error('Failed to add item');
    return res.json();
  }

  async function refreshCart() {
    const api = cartApi();
    if (api && typeof api.refreshCount === 'function') {
      try { await api.refreshCount(); } catch (e) { /* non-fatal */ }
    }
    document.dispatchEvent(new Event('cart:open'));
  }

  /* --- scroll reveal --- */
  (function () {
    const targets = root.querySelectorAll('[data-elp-reveal]');
    if (!targets.length) return;

    root.setAttribute('data-elp-reveal-ready', '');

    if (!('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('is-revealed'));
      return;
    }
    const obs = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        entry.target.dispatchEvent(new CustomEvent('elp:revealed'));
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    targets.forEach(el => obs.observe(el));
  })();

  /* --- gallery --- */
  // Slides can be filtered down to one colour's images. The variant picker
  // dispatches `elp:gallery` with the slide indexes to show and which to focus.
  (function () {
    const gallery = root.querySelector('[data-elp-gallery]');
    if (!gallery) return;

    const slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-elp-slide]'));
    const dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-elp-dot]'));
    const prev = gallery.querySelector('[data-elp-prev]');
    const next = gallery.querySelector('[data-elp-next]');
    const dotsWrap = gallery.querySelector('.elp-gallery__dots');
    if (!slides.length) return;

    let visible = slides.map((_, i) => i);
    let pos = 0;

    function show(nextPos) {
      if (!visible.length) return;
      pos = (nextPos + visible.length) % visible.length;
      const active = visible[pos];
      slides.forEach((s, i) => s.classList.toggle('is-active', i === active));
      // One dot per visible slide; the rest are parked.
      dots.forEach((d, i) => {
        d.hidden = i >= visible.length;
        d.classList.toggle('is-active', i === pos);
      });
    }

    function setVisible(list, focusSlide) {
      visible = (list && list.length) ? list.slice() : slides.map((_, i) => i);
      const idx = (focusSlide === null || focusSlide === undefined) ? -1 : visible.indexOf(focusSlide);
      show(idx > -1 ? idx : 0);

      const many = visible.length > 1;
      if (prev) prev.hidden = !many;
      if (next) next.hidden = !many;
      if (dotsWrap) dotsWrap.hidden = !many;
    }

    if (prev) prev.addEventListener('click', () => show(pos - 1));
    if (next) next.addEventListener('click', () => show(pos + 1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => { if (i < visible.length) show(i); }));

    let startX = null;
    gallery.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    gallery.addEventListener('touchend', e => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) show(dx < 0 ? pos + 1 : pos - 1);
      startX = null;
    }, { passive: true });

    root.addEventListener('elp:gallery', e => {
      setVisible(e.detail && e.detail.visible, e.detail && e.detail.focus);
    });

    setVisible(null, null);
  })();

  /* --- quantity (hero stepper, sticky select, kept in sync) --- */
  const form = root.querySelector('[data-elp-form]');
  const QTY_MIN = 1;
  const QTY_MAX = 10;

  function setQuantity(value) {
    const qty = Math.max(QTY_MIN, Math.min(QTY_MAX, parseInt(value, 10) || 1));
    if (form) {
      const qtyInput = form.querySelector('[name="quantity"]');
      if (qtyInput) qtyInput.value = qty;
    }
    document.querySelectorAll('[data-elp-qty]').forEach(sel => { sel.value = qty; });
    document.querySelectorAll('[data-elp-qty-display]').forEach(disp => { disp.textContent = qty; });
    return qty;
  }

  (function () {
    // Sticky quantity dropdown (unchanged control).
    document.querySelectorAll('[data-elp-qty]').forEach(sel => {
      sel.addEventListener('change', () => setQuantity(sel.value));
    });

    // Hero quantity stepper.
    document.querySelectorAll('[data-elp-qty-stepper]').forEach(stepper => {
      const display = stepper.querySelector('[data-elp-qty-display]');
      const minus = stepper.querySelector('[data-elp-qty-decrease]');
      const plus = stepper.querySelector('[data-elp-qty-increase]');

      function current() {
        return parseInt(display ? display.textContent : '1', 10) || 1;
      }

      if (minus) {
        minus.addEventListener('click', () => {
          setQuantity(current() - 1);
        });
      }
      if (plus) {
        plus.addEventListener('click', () => {
          setQuantity(current() + 1);
        });
      }
    });
  })();

  /* --- variant picker (full option-combination matching) --- */
  (function () {
    if (!form) return;
    const dataEl = form.querySelector('[data-elp-variants]');
    const groups = form.querySelectorAll('[data-elp-option]');
    if (!dataEl || !groups.length) return;

    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!Array.isArray(variants) || !variants.length) return;

    const variantInput = form.querySelector('[name="id"]');
    const priceEl = document.querySelector('[data-elp-price]');
    const saleLine = document.querySelector('[data-elp-sale-line]');
    const compareEl = document.querySelector('[data-elp-compare]');
    const discountEl = document.querySelector('[data-elp-discount]');
    const atcButtons = document.querySelectorAll('[data-elp-atc]');

    const selected = [];
    groups.forEach(group => {
      const idx = parseInt(group.dataset.elpOption, 10);
      const active = group.querySelector('.is-active');
      selected[idx] = active ? active.dataset.value : null;
    });

    // Which option is the colour, and which product images belong to each value.
    const colorGroup = form.querySelector('[data-elp-is-color]');
    const colorIndex = colorGroup ? parseInt(colorGroup.dataset.elpOption, 10) : -1;

    const colorSlides = {};   // colour -> [slide index]
    const assigned = new Set();
    if (colorIndex > -1) {
      variants.forEach(variant => {
        const img = variant.featured_image;
        if (!img || !img.position) return;
        const slide = img.position - 1;
        const color = variant.options[colorIndex];
        if (!color) return;
        if (!colorSlides[color]) colorSlides[color] = [];
        if (colorSlides[color].indexOf(slide) === -1) colorSlides[color].push(slide);
        assigned.add(slide);
      });
    }

    // Images not tied to any colour are shared, so they stay in every gallery.
    function slidesForColor(color) {
      const own = colorSlides[color];
      if (!own || !own.length) return null;          // nothing assigned: show everything
      const shared = [];
      const totalSlides = root.querySelectorAll('[data-elp-slide]').length;
      for (let i = 0; i < totalSlides; i++) {
        if (!assigned.has(i)) shared.push(i);
      }
      return own.concat(shared).sort((a, b) => a - b);
    }

    function syncGallery(variant) {
      if (colorIndex < 0) return;
      const color = selected[colorIndex];
      const focus = variant && variant.featured_image && variant.featured_image.position
        ? variant.featured_image.position - 1
        : null;
      root.dispatchEvent(new CustomEvent('elp:gallery', {
        detail: { visible: slidesForColor(color), focus: focus }
      }));
    }

    const matches = (variant, values) =>
      values.every((val, i) => val === null || variant.options[i] === val);

    function markAvailability() {
      groups.forEach(group => {
        const idx = parseInt(group.dataset.elpOption, 10);
        group.querySelectorAll('[data-value]').forEach(btn => {
          const probe = selected.slice();
          probe[idx] = btn.dataset.value;
          const reachable = variants.some(v => matches(v, probe) && v.available);
          btn.classList.toggle('is-unavailable', !reachable);
        });
      });
    }

    function render() {
      const variant = variants.find(v => matches(v, selected)) || null;
      const available = !!(variant && variant.available);
      const price = variant ? variant.price : 0;
      const compare = variant ? (variant.compare_at_price || 0) : 0;

      if (variantInput) variantInput.value = variant ? variant.id : '';

      if (priceEl && variant) priceEl.textContent = money(price);

      if (saleLine) {
        if (available && compare > price) {
          if (compareEl) compareEl.textContent = money(compare);
          if (discountEl) discountEl.textContent = Math.round(((compare - price) / compare) * 100) + '% off';
          saleLine.hidden = false;
        } else {
          saleLine.hidden = true;
        }
      }

      const label = available ? root.dataset.elpAtcText + ' · ' + money(price) : 'Sold Out';
      atcButtons.forEach(btn => {
        btn.dataset.label = label;
        btn.disabled = !available;
        btn.classList.toggle('is-soldout', !available);
        btn.classList.remove('is-added');
        btn.textContent = label;
      });

      markAvailability();
      syncGallery(variant);
    }

    groups.forEach(group => {
      const idx = parseInt(group.dataset.elpOption, 10);
      group.querySelectorAll('[data-value]').forEach(btn => {
        btn.addEventListener('click', () => {
          selected[idx] = btn.dataset.value;
          group.querySelectorAll('[data-value]').forEach(s => s.classList.remove('is-active'));
          btn.classList.add('is-active');
          const label = group.querySelector('[data-elp-option-value]');
          if (label) label.textContent = btn.dataset.value;
          render();
        });
      });
    });

    markAvailability();
    syncGallery(variants.find(v => matches(v, selected)) || null);
  })();

  /* --- add to cart --- */
  (function () {
    if (!form) return;
    const buttons = document.querySelectorAll('[data-elp-atc]');
    if (!buttons.length) return;
    let resetTimer;

    async function handle() {
      const idField = form.querySelector('[name="id"]');
      const qtyField = form.querySelector('[name="quantity"]');
      const variantId = idField ? idField.value : '';
      const quantity = parseInt(qtyField ? qtyField.value : '1', 10) || 1;
      if (!variantId) return;

      clearTimeout(resetTimer);
      buttons.forEach(b => { b.disabled = true; b.textContent = 'Adding…'; });

      try {
        await addToCart(variantId, quantity);
        buttons.forEach(b => { b.textContent = 'Added ✓'; b.classList.add('is-added'); });
        await refreshCart();
        resetTimer = setTimeout(() => {
          buttons.forEach(b => {
            b.disabled = false;
            b.classList.remove('is-added');
            b.textContent = b.dataset.label || 'Add to Cart';
          });
        }, 1200);
      } catch (err) {
        console.error('Everyday Loopie PDP — add to cart failed', err);
        buttons.forEach(b => {
          b.disabled = false;
          b.classList.remove('is-added');
          b.textContent = b.dataset.label || 'Add to Cart';
        });
      }
    }

    buttons.forEach(btn => btn.addEventListener('click', handle));
  })();

  /* --- find your hold --- */
  (function () {
    const hold = root.querySelector('[data-elp-hold]');
    if (!hold) return;
    const input = hold.querySelector('[data-elp-hold-input]');
    const fill = hold.querySelector('[data-elp-hold-fill]');
    const thumb = hold.querySelector('[data-elp-hold-thumb]');
    const ticks = hold.querySelectorAll('[data-elp-hold-tick]');
    const nums = hold.querySelectorAll('[data-elp-hold-num]');
    const panels = hold.querySelectorAll('[data-elp-hold-panel]');
    if (!input || !fill || !thumb) return;

    const max = parseInt(hold.dataset.max || '5', 10) || 1;
    let hinted = false;

    function render(level) {
      const pct = (level / max) * 100;
      fill.style.width = pct + '%';
      thumb.style.left = 'calc(' + pct + '% - 11px)';
      ticks.forEach((t, i) => t.classList.toggle('is-on', i <= level));
      nums.forEach((n, i) => n.classList.toggle('is-active', i === level));
      panels.forEach((p, i) => p.classList.toggle('is-active', i === level));
    }

    function stopHint() {
      if (!hinted) return;
      thumb.classList.remove('is-hinting');
      hinted = false;
    }

    input.addEventListener('input', () => { stopHint(); render(parseInt(input.value, 10)); });
    input.addEventListener('change', () => render(parseInt(input.value, 10)));

    // Nudge the thumb once, when the widget first scrolls into view
    hold.addEventListener('elp:revealed', () => {
      hinted = true;
      thumb.classList.add('is-hinting');
      thumb.addEventListener('animationend', stopHint, { once: true });
    }, { once: true });

    render(parseInt(input.value, 10));
  })();

  /* --- reviews --- */
  (function () {
    const wrap = root.querySelector('[data-elp-reviews]');
    if (!wrap) return;
    const reviews = wrap.querySelectorAll('[data-elp-review]');
    const dots = wrap.querySelectorAll('[data-elp-review-dot]');
    if (reviews.length < 2) return;

    let index = 0;
    let timer;

    function show(next) {
      index = (next + reviews.length) % reviews.length;
      reviews.forEach((r, i) => r.classList.toggle('is-active', i === index));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    }
    function startAuto() {
      if (wrap.dataset.autorotate !== 'true') return;
      clearInterval(timer);
      timer = setInterval(() => show(index + 1), 4500);
    }

    dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); startAuto(); }));
    startAuto();
  })();

  /* --- FAQ --- */
  root.querySelectorAll('[data-elp-faq-q]').forEach(btn => {
    btn.addEventListener('click', () => {
      const item = btn.closest('.elp-faq__item');
      const card = item.closest('.elp-faq__card');
      const isOpen = item.classList.contains('is-open');
      card.querySelectorAll('.elp-faq__item.is-open').forEach(open => {
        open.classList.remove('is-open');
        const q = open.querySelector('[data-elp-faq-q]');
        if (q) q.setAttribute('aria-expanded', 'false');
      });
      if (!isOpen) {
        item.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });

  /* --- sticky bar visibility --- */
  (function () {
    const bar = document.querySelector('[data-elp-sticky]');
    const mainAtc = root.querySelector('[data-elp-atc-main]');
    if (!bar || !mainAtc || !('IntersectionObserver' in window)) return;
    const obs = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-hidden', entry.isIntersecting);
    }, { threshold: 0.1 });
    obs.observe(mainAtc);
  })();
})();
